import {
  createContext,
  FunctionComponent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useApolloClient } from "@apollo/client";
import { useToast } from "@chakra-ui/react";
import {
  AuthError,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  getAuth,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  signInWithCustomToken as firebaseSignInWithCustomToken,
  updatePassword,
} from "firebase/auth";
import {
  MeDocument,
  MeQuery,
  MeQueryVariables,
  Organization,
} from "@/generated/generated-graphql";
import { useRouter } from "next/router";
import FirebaseApp from "@/utils/firebase";
import { ROUTES } from "@/constants/routes";
import { isImpersonating, clearImpersonationToken } from "@/utils/impersonation";

interface EmailAndPasswordInput {
  email: string;
  password: string;
}

interface ConfirmPasswordResetInput {
  resetCode: string;
  password: string;
}

interface UserContextType {
  isLoading: boolean;
  isFirebaseInitializing: boolean;
  userData: Organization | null;
  signOut: (shouldRedirect?: boolean) => Promise<void>;
  refetchAndSetUserData: () => Promise<void>;
  sendResetPasswordEmail: ({ email }: { email: string }) => Promise<boolean>;
  confirmPasswordReset: ({
    resetCode,
    password,
  }: ConfirmPasswordResetInput) => Promise<boolean>;
  changePassword: (newPassword: string) => Promise<void> | null;
  token: string | null;
  signInWithEmailAndPassword: (
    input: EmailAndPasswordInput,
  ) => Promise<void> | null;
  signInWithCustomToken: (token: string) => Promise<void> | null;
  hasExtraPackage: (extraPackage: string) => boolean;
}

interface Props {
  children: ReactNode;
}

const FirebaseAuth = getAuth(FirebaseApp);

const UserContext = createContext({} as UserContextType);

export const useAuth = (): UserContextType => useContext(UserContext);

const UserProvider: FunctionComponent<Props> = ({ children }) => {
  const [userData, setUserData] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFirebaseInitializing, setIsFirebaseInitializing] = useState(true);
  const [userFirebaseToken, setUserFirebaseToken] = useState<string | null>(
    null,
  );

  const router = useRouter();
  const toast = useToast();
  const apolloClient = useApolloClient();

  const signInWithEmailAndPassword = useCallback(
    async ({ email, password }: EmailAndPasswordInput) => {
      try {
        setIsLoading(true);
        await firebaseSignInWithEmailAndPassword(FirebaseAuth, email, password);
      } catch (error) {
        const err = error as AuthError;
        switch (err.code) {
          case "auth/invalid-email":
          case "auth/wrong-password":
          case "auth/user-not-found":
            toast({
              title: "Nesprávny email alebo heslo.",
              status: "error",
              duration: 8000,
              isClosable: true,
              position: "top",
            });
            break;
          case "auth/too-many-requests":
            toast({
              title: "Príliš veľa pokusov. Skúste neskôr.",
              description: "Ak sa problém opakuje, kontaktuje podporu.",
              status: "error",
              duration: 8000,
              isClosable: true,
              position: "top",
            });
            break;
          default:
            toast({
              title: "Prihlásenie sa nepodarilo",
              description: "Ak sa problém opakuje, kontaktuje podporu.",
              status: "error",
              duration: 8000,
              isClosable: true,
              position: "top",
            });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [toast],
  );

  const signInWithCustomToken = useCallback(
    async (customToken: string) => {
      try {
        setIsLoading(true);
        await firebaseSignInWithCustomToken(FirebaseAuth, customToken);
      } catch (error) {
        const err = error as AuthError;
        toast({
          title: "Prihlásenie sa nepodarilo",
          description: err.code,
          status: "error",
          duration: 8000,
          isClosable: true,
          position: "top",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [toast],
  );

  const signOut = useCallback(
    async (shouldRedirect = true) => {
      try {
        setIsLoading(true);

        // Clear impersonation token if present
        if (isImpersonating()) {
          clearImpersonationToken();
        }

        await FirebaseAuth.signOut();
        if (shouldRedirect) {
          router.push(ROUTES.SIGN_IN());
        }
      } catch {
        toast({
          title: "Odhlasenie zlyhalo",
          status: "error",
          duration: 6000,
          isClosable: true,
          position: "top",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [router, toast],
  );

  const changePassword = useCallback(
    (newPassword: string) =>
      FirebaseAuth.currentUser &&
      updatePassword(FirebaseAuth.currentUser, newPassword),
    [],
  );

  const refetchAndSetUserData = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await apolloClient.query<MeQuery, MeQueryVariables>({
        query: MeDocument,
        fetchPolicy: "network-only",
      });

      // console.log('UserProvider.tsx (190)', response);

      if (response.data.me) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore // TODO: Fix this type error
        setUserData(response.data.me);
      } else {
        // If impersonating and query failed, clear the token
        if (isImpersonating()) {
          clearImpersonationToken();
          toast({
            title: "Impersonácia zlyhala",
            description: "Impersonation token je neplatný alebo expiroval.",
            status: "error",
            duration: 6000,
            isClosable: true,
            position: "top",
          });
          router.push(ROUTES.SIGN_IN());
        } else {
          await signOut();
        }
        setUserData(null);
      }
    } catch (error) {
      // If impersonating and query failed, clear the token
      if (isImpersonating()) {
        clearImpersonationToken();
        toast({
          title: "Impersonácia zlyhala",
          description: "Impersonation token je neplatný alebo expiroval.",
          status: "error",
          duration: 6000,
          isClosable: true,
          position: "top",
        });
        router.push(ROUTES.SIGN_IN());
      } else {
        await signOut();
      }
    } finally {
      setIsLoading(false);
    }
  }, [apolloClient, signOut, toast, router]);

  const sendResetPasswordEmail = useCallback(
    async ({ email }: { email: string }) => {
      try {
        setIsLoading(true);
        await firebaseSendPasswordResetEmail(FirebaseAuth, email);
        setIsLoading(false);
        return true;
      } catch {
        setIsLoading(false);
        return false;
      }
    },
    [],
  );

  const confirmPasswordReset = useCallback(
    async ({ resetCode, password }: ConfirmPasswordResetInput) => {
      try {
        setIsLoading(true);
        await firebaseConfirmPasswordReset(FirebaseAuth, resetCode, password);
        setIsLoading(false);
        return true;
      } catch (error) {
        setIsLoading(false);
        return false;
      }
    },
    [],
  );

  useEffect(() => {
    const subscribe = FirebaseAuth.onAuthStateChanged((firebaseUser) => {
      if (firebaseUser) {
        (async () => {
          await refetchAndSetUserData();
          setIsFirebaseInitializing(false);

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          setUserFirebaseToken(firebaseUser?.accessToken);
        })();
      } else if (isImpersonating()) {
        // If no Firebase user but we have impersonation token, fetch data
        (async () => {
          await refetchAndSetUserData();
          setIsFirebaseInitializing(false);
        })();
      } else {
        setUserData(null);
        setIsFirebaseInitializing(false);
      }
    });
    return subscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasExtraPackage = useCallback(
    (extraPackage: string) =>
      userData?.activeExtraPackages.includes(extraPackage) ?? false,
    [userData?.activeExtraPackages],
  );

  return (
    <UserContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        userData,
        isLoading,
        isFirebaseInitializing,
        signInWithEmailAndPassword,
        signOut,
        refetchAndSetUserData,
        sendResetPasswordEmail,
        confirmPasswordReset,
        changePassword,
        signInWithCustomToken,
        token: userFirebaseToken,
        hasExtraPackage,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
export default UserProvider;
